import type { ReactNode } from 'react'
import React, { useMemo, createContext, useContext, useEffect, useCallback } from 'react'
import { useParams } from '@fs/zion-router'
import type { ResearchHelpData } from '../../data-service/useResearchHelpData'
import useResearchHelpData from '../../data-service/useResearchHelpData'
import { useFocusFamilyMembersContext } from './FocusFamilyMembersContext'

type ResearchHelpContextValue = {
  researchHelpData: ResearchHelpData
}

const ResearchHelpContext = createContext<ResearchHelpContextValue>({
  researchHelpData: {
    loadingRecordHints: true,
    loadingPossibleDuplicates: true,
    loadingDismissedPossibleDuplicates: true,
    count: 0,
    dismissedCount: 0,
  },
})

type Props = {
  children: ReactNode
  isResearcherGroup?: boolean
  treeId?: string
}

export default function ResearchHelpProvider({ children, isResearcherGroup, treeId }: Props): JSX.Element {
  const { focusFamilyMembers, loadingFamilyMembers } = useFocusFamilyMembersContext()
  const { pid } = useParams()
  const researchHelpData = useResearchHelpData(
    pid,
    focusFamilyMembers.researchHelp,
    isResearcherGroup || loadingFamilyMembers,
    treeId
  )
  const value = useMemo(
    () => ({ researchHelpData, focusFamilyMembers, loadingFamilyMembers }),
    [researchHelpData, focusFamilyMembers, loadingFamilyMembers]
  )
  const fetchData = useCallback(() => {
    researchHelpData.refetchRecordHints?.()
  }, [researchHelpData])

  useEffect(() => {
    window.addEventListener('focus', fetchData)

    return () => {
      window.removeEventListener('focus', fetchData)
    }
  }, [fetchData])

  return <ResearchHelpContext.Provider value={value}>{children}</ResearchHelpContext.Provider>
}

export const useResearchHelpContext = (): ResearchHelpContextValue => {
  return useContext(ResearchHelpContext)
}
